/**
 * A v4 UUID that works outside a secure context.
 *
 * `crypto.randomUUID()` is defined *only* in a secure context. Browsers special-case
 * `http://localhost` as secure, so it is present in local development and absent the
 * moment the same build is opened over a LAN address — `http://10.8.31.118:8080` on a
 * phone. There it is `undefined`, and calling it throws
 * `TypeError: crypto.randomUUID is not a function`.
 *
 * That failure was silent and badly disguised. `getDeviceId` runs inside `request`
 * before any fetch is issued, so the throw escaped every API call — including sign-in,
 * where the catch reported "Email or password not recognised" for what was actually a
 * missing browser API and a request that never left the phone.
 *
 * `crypto.getRandomValues` is *not* gated on secure context, so the fallback is still
 * cryptographically random. `Math.random` is the last resort for an engine with no
 * `crypto` at all; the id is a device label, not a secret (D18), so a weak one is worth
 * more than a crash.
 *
 * Delete this the day the app is only ever served over TLS — but the field kit is
 * tested on a LAN long before a certificate exists, and that is exactly when the app
 * has to work.
 */
export function randomUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }

  // Version 4, variant 10xx — the two fields that make this a well-formed v4 rather
  // than sixteen random bytes wearing hyphens.
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;

  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
